import React, { useEffect } from "react";
import { IconClose } from "./Icons";

const Section = ({ title, children }) => (
  <section style={{ marginBottom: 28 }}>
    <h3 style={{ fontSize: "1.25rem", marginBottom: 8 }}>{title}</h3>
    <div style={{ fontSize: "0.94rem", lineHeight: 1.75 }}>{children}</div>
  </section>
);

const Privacy = () => (
  <>
    <Section title="Who is responsible for your data">
      <p>
        Casa Carmela Positano, V. G. Marconi 196, 84017 Positano (SA), Italy — email{" "}
        <a href="mailto:casacarmelapositano@gmail.com">casacarmelapositano@gmail.com</a>. This is the
        data controller for the personal data described below.
      </p>
    </Section>

    <Section title="What we collect and why">
      <ul style={{ listStyle: "disc", paddingLeft: 20, display: "grid", gap: 8 }}>
        <li>
          <strong>Contact form.</strong> Name, email address and message. Legal basis: your consent
          and our legitimate interest in answering enquiries (Art. 6(1)(a) and (f) GDPR). Messages
          are delivered through EmailJS and stored in our email inbox. We keep them only as long as
          needed to handle your request.
        </li>
        <li>
          <strong>Analytics (optional).</strong> Google Analytics 4 with IP anonymisation. Legal
          basis: your consent (Art. 6(1)(a) GDPR). Loaded only after you press “Accept all” or enable
          Analytics. Cookies: <code>_ga</code>, <code>_ga_*</code> (up to 24 months).
        </li>
        <li>
          <strong>Google Maps (optional).</strong> Loading the embedded map sends your IP address and
          device information to Google. Legal basis: your consent. The map is not loaded until you
          ask for it.
        </li>
        <li>
          <strong>Cookie choice.</strong> Stored in your browser's localStorage (not a cookie) so we
          can remember your decision. Legal basis: strictly necessary.
        </li>
      </ul>
    </Section>

    <Section title="Who we share data with">
      <p>
        EmailJS (email delivery), Google Ireland Ltd. / Google LLC (analytics and maps), and our
        hosting provider. Some of these providers may process data outside the EU/EEA; transfers rely
        on the European Commission's Standard Contractual Clauses and, where applicable, the EU–US
        Data Privacy Framework.
      </p>
    </Section>

    <Section title="Your rights">
      <p>
        You have the right to access, rectify, erase, restrict or object to the processing of your
        data, and to data portability. Where processing is based on consent you may withdraw it at
        any time using “Cookie settings” in the footer. You may also lodge a complaint with the
        Italian supervisory authority (Garante per la protezione dei dati personali,{" "}
        <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">
          garanteprivacy.it
        </a>
        ).
      </p>
    </Section>

    <Section title="Retention">
      <p>
        Enquiries are kept for up to 24 months. Analytics data is retained for 14 months. Consent
        records are kept until you clear them or for 12 months, after which we ask again.
      </p>
    </Section>

    <Section title="Reviews shown on this site">
      <p>
        Guest reviews are published on Airbnb and Booking.com by those guests. We display a selection
        of them here together with the platform's aggregate rating. Reviews are attributed to the
        first name used on the platform; no other personal data is shown. To have a review removed,
        contact us or the platform where it was posted.
      </p>
    </Section>
  </>
);

const Cookies = () => (
  <>
    <Section title="What we store">
      <p>
        We do not set any cookie before you make a choice. The table below lists everything that can
        be stored on your device.
      </p>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid rgba(23,40,42,.2)" }}>
              <th style={{ padding: "10px 8px" }}>Name</th>
              <th style={{ padding: "10px 8px" }}>Type</th>
              <th style={{ padding: "10px 8px" }}>Purpose</th>
              <th style={{ padding: "10px 8px" }}>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: "1px solid rgba(23,40,42,.08)" }}>
              <td style={{ padding: "10px 8px" }}>
                <code>cc_consent_v1</code>
              </td>
              <td style={{ padding: "10px 8px" }}>localStorage</td>
              <td style={{ padding: "10px 8px" }}>Remembers your cookie choice</td>
              <td style={{ padding: "10px 8px" }}>Until cleared</td>
            </tr>
            <tr style={{ borderBottom: "1px solid rgba(23,40,42,.08)" }}>
              <td style={{ padding: "10px 8px" }}>
                <code>_ga</code>, <code>_ga_*</code>
              </td>
              <td style={{ padding: "10px 8px" }}>Cookie (Google)</td>
              <td style={{ padding: "10px 8px" }}>Anonymous usage statistics</td>
              <td style={{ padding: "10px 8px" }}>Up to 24 months</td>
            </tr>
            <tr>
              <td style={{ padding: "10px 8px" }}>Google Maps</td>
              <td style={{ padding: "10px 8px" }}>Third-party embed</td>
              <td style={{ padding: "10px 8px" }}>Displays the location map</td>
              <td style={{ padding: "10px 8px" }}>Session</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>

    <Section title="Changing your mind">
      <p>
        Use the “Cookie settings” button in the footer at any time. Rejecting analytics also deletes
        the Google Analytics cookies we can reach.
      </p>
    </Section>
  </>
);

const Imprint = () => (
  <>
    <Section title="Property details">
      <p>
        Casa Carmela Positano — V. G. Marconi 196, 84017 Positano (SA), Italy.
        <br />
        CIN: 15065100LOB0606 · CIR: IT065100C23BE6SK6P
      </p>
    </Section>
    <Section title="Contact">
      <p>
        <a href="mailto:casacarmelapositano@gmail.com">casacarmelapositano@gmail.com</a>
      </p>
    </Section>
    <Section title="Credits">
      <p>
        Design and development by Angelo Mandara. Based on a template by Issaaf Kattan and
        TemplateWire.
      </p>
    </Section>
  </>
);

const TITLES = {
  privacy: "Privacy notice",
  cookies: "Cookie policy",
  imprint: "Legal notice",
};

const LegalModal = ({ page, onClose }) => {
  useEffect(() => {
    // Only lock scrolling while a document is actually open. This component
    // stays mounted with page === null, so an unguarded effect would leave
    // body { overflow: hidden } applied to the whole site.
    if (!page) return undefined;

    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [page, onClose]);

  if (!page) return null;

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-title"
      style={{ background: "rgba(8,24,24,.72)", padding: "24px 16px" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "var(--cream)",
          borderRadius: "var(--radius-lg)",
          maxWidth: 760,
          width: "100%",
          maxHeight: "100%",
          overflowY: "auto",
          padding: "32px clamp(20px, 4vw, 44px)",
          margin: "auto",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            marginBottom: 20,
          }}
        >
          <h2 id="legal-title" style={{ margin: 0, fontSize: "1.9rem" }}>
            {TITLES[page]}
          </h2>
          <button
            type="button"
            className="drawer__close"
            onClick={onClose}
            aria-label="Close"
            style={{ flexShrink: 0 }}
          >
            <IconClose />
          </button>
        </div>

        <p style={{ fontSize: "0.8rem", color: "var(--muted)", marginBottom: 24 }}>
          Last updated: 18 October 2026
        </p>

        {page === "privacy" && <Privacy />}
        {page === "cookies" && <Cookies />}
        {page === "imprint" && <Imprint />}
      </div>
    </div>
  );
};

export default LegalModal;
